import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { BadgeCheck } from 'lucide-react';
import { z } from 'zod';
import { FormTextareaField } from '@/components/common/FormDialog';
import {
  DECISION_COMMENT_MIN_LENGTH,
  DONATION_COMMENT_MAX_LENGTH,
  DONATION_DECISION,
} from '../constants/marketPromotion.constants';
import { useDecideDonation } from '../hooks/useMpMutations';
import type { MpApprovalDetail } from '../types/marketPromotion.types';
import { DonationRows } from './DonationDetailsCard';
import { RECOMMEND_COMMENT_ROW } from './donationRows';
import { DonationFormCard } from './DonationFormCard';

const approveFormSchema = z.object({
  comment: z
    .string()
    .trim()
    .min(
      DECISION_COMMENT_MIN_LENGTH,
      `Write at least ${String(DECISION_COMMENT_MIN_LENGTH)} characters`,
    )
    .max(DONATION_COMMENT_MAX_LENGTH),
});

type ApproveFormValues = z.infer<typeof approveFormSchema>;

type DonationVerdict = typeof DONATION_DECISION.APPROVED | typeof DONATION_DECISION.REJECTED;

interface ApproveDonationFormProps {
  donation: MpApprovalDetail;
  onClose: () => void;
  onDone: () => void;
}

export function ApproveDonationForm({ donation, onClose, onDone }: ApproveDonationFormProps) {
  const form = useForm<ApproveFormValues>({
    resolver: zodResolver(approveFormSchema),
    defaultValues: { comment: '' },
  });
  const decideMutation = useDecideDonation(onDone);
  const pendingVerdict = decideMutation.isPending
    ? decideMutation.variables.payload.approveTypeNo
    : undefined;

  function decide(approveTypeNo: DonationVerdict, values: ApproveFormValues) {
    decideMutation.mutate({
      detailId: donation.detailId,
      payload: { approveTypeNo, approvalComments: values.comment.trim() },
    });
  }

  return (
    <DonationFormCard
      donation={donation}
      title="Approve Donation"
      icon={BadgeCheck}
      form={form}
      onSubmit={(values) => decide(DONATION_DECISION.APPROVED, values)}
      onClose={onClose}
      submitLabel="Approve"
      isSubmitPending={pendingVerdict === DONATION_DECISION.APPROVED}
      rejectAction={{
        onClick: () =>
          void form.handleSubmit((values) => decide(DONATION_DECISION.REJECTED, values))(),
        isPending: pendingVerdict === DONATION_DECISION.REJECTED,
      }}
      decisionRows={
        <>
          <DonationRows donation={donation} rows={[RECOMMEND_COMMENT_ROW]} />
          <FormTextareaField
            control={form.control}
            name="comment"
            label="Your Comment"
            placeholder={`At least ${String(DECISION_COMMENT_MIN_LENGTH)} characters`}
            maxLength={DONATION_COMMENT_MAX_LENGTH}
            isRequired
          />
        </>
      }
      hasPictureFields
    />
  );
}
